import React from "react";
import useCustomScrollbar from "../../../../styles/useCustomScrollbar";

interface NutritionData {
  total_cals: number;
  cals_units: string;
  fat: number;
  fat_units: string;
  carbs: number;
  carbs_units: string;
  protein: number;
  protein_units: string;
  GL: number;
  GL_units: string;
  CPF: string;
}

interface LoggedData {
  total_calories_logged: string;
  total_fat_logged: string;
  total_carbs_logged: string;
  total_GL_logged: string;
  total_AAFAdj_protein_logged: string;
  total_CPF: string;
}

interface NutritionStatsProps {
  nutritionData?: NutritionData;
  loggedData?: LoggedData;
  loading?: boolean;
}

const NutritionStats: React.FC<NutritionStatsProps> = ({ 
  nutritionData, 
  loggedData,
  loading = false 
}) => {
  const calculatePercentage = (logged: number, target: number): number => {
    if (target === 0) return 0;
    return Math.min((logged / target) * 100, 100);
  };

  const parseCPF = (cpfString: string): { carbs: number; protein: number; fat: number } => {
    const parts = cpfString.split(':');
    return {
      carbs: parseFloat(parts[0]) || 0,
      protein: parseFloat(parts[1]) || 0,
      fat: parseFloat(parts[2]) || 0,
    };
  };

  // Loading state
  if (loading) {
    return (
      <div className="mx-6 mt-6">
        <div className="bg-white px-4 py-4 border-[0.5px] border-[#F3F4F6] rounded-md">
          <div className="grid grid-cols-6 gap-6 w-full animate-pulse">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i}>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-[3px] bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Check if we have both nutrition data and logged data
  const hasNutritionData = nutritionData && Object.keys(nutritionData).length > 0;
  const hasLoggedData = loggedData && Object.keys(loggedData).length > 0;

  console.log('NutritionStats: hasNutritionData:', hasNutritionData, 'hasLoggedData:', hasLoggedData);
  console.log('NutritionStats: nutritionData:', nutritionData);
  console.log('NutritionStats: loggedData:', loggedData);

  // If we don't have either data set, show empty state
  if (!hasNutritionData || !hasLoggedData) {
    return (
      <div className="mx-6">
        <div className="bg-white px-4 py-4 border-[0.5px] border-[#F3F4F6] rounded-md">
          <p className="text-sm text-gray-500 text-center">No nutrition data available</p>
        </div>
      </div>
    );
  }

  const targetCPF = parseCPF(nutritionData.CPF);
  const loggedCPF = parseCPF(loggedData.total_CPF);
  const cpfTotal = targetCPF.carbs + targetCPF.protein + targetCPF.fat;
  const cpfPercentage = cpfTotal > 0 
    ? ((loggedCPF.carbs + loggedCPF.protein + loggedCPF.fat) / cpfTotal) * 100 
    : 0;

  return (
    <div className="mx-6 mt-3">
      <div className="bg-white px-4 py-4 border-[0.5px] border-[#F3F4F6] rounded-md overflow-x-auto custom-scrollbar-overlay">
        <div className="grid grid-cols-6 gap-6 w-full">
          {/* Cal */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-[#1F1F1F] text-[12px] 2xl:[14px] font-medium font-inter">
                Cal <span className="text-[#8D8D8D]"> ({nutritionData.cals_units})</span>
              </span>
              <span className="text-[#1F1F1F] text-[12px] 2xl:[14px] font-medium font-inter">
                {parseFloat(loggedData.total_calories_logged || '0').toFixed(0)}/{nutritionData.total_cals.toFixed(0)}
              </span>
            </div>
            <div className="w-full h-[3px] bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#B44AE6]" 
                style={{ 
                  width: `${calculatePercentage(
                    parseFloat(loggedData.total_calories_logged || '0'), 
                    nutritionData.total_cals
                  )}%` 
                }} 
              />
            </div>
          </div>

          {/* Protein */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-[#1F1F1F] text-[12px] 2xl:[14px] font-medium font-inter">
                (A)Prot. <span className="text-[#8D8D8D]"> ({nutritionData.protein_units})</span>
              </span>
              <span className="text-[#1F1F1F] text-[12px] 2xl:[14px] font-medium font-inter">
                {parseFloat(loggedData.total_AAFAdj_protein_logged || '0').toFixed(0)}/{nutritionData.protein.toFixed(0)}
              </span>
            </div>
            <div className="w-full h-[3px] bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#6BBDFA]" 
                style={{ 
                  width: `${calculatePercentage(
                    parseFloat(loggedData.total_AAFAdj_protein_logged || '0'), 
                    nutritionData.protein
                  )}%` 
                }} 
              />
            </div>
          </div>

          {/* Carb */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-[#1F1F1F] text-[12px] 2xl:[14px] font-medium font-inter">
                Carb <span className="text-[#8D8D8D]"> ({nutritionData.carbs_units})</span>
              </span>
              <span className="text-[#1F1F1F] text-[12px] 2xl:[14px] font-medium font-inter">
                {parseFloat(loggedData.total_carbs_logged || '0').toFixed(0)}/{nutritionData.carbs.toFixed(0)}
              </span>
            </div>
            <div className="w-full h-[3px] bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#FFB532]" 
                style={{ 
                  width: `${calculatePercentage(
                    parseFloat(loggedData.total_carbs_logged || '0'), 
                    nutritionData.carbs
                  )}%` 
                }} 
              />
            </div>
          </div>

          {/* Fat */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-[#1F1F1F] text-[12px] 2xl:[14px] font-medium font-inter">
                Fat <span className="text-[#8D8D8D]"> ({nutritionData.fat_units})</span>
              </span>
              <span className="text-[#1F1F1F] text-[12px] 2xl:[14px] font-medium font-inter">
                {parseFloat(loggedData.total_fat_logged || '0').toFixed(0)}/{nutritionData.fat.toFixed(0)}
              </span>
            </div>
            <div className="w-full h-[3px] bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#F25BEC]" 
                style={{ 
                  width: `${calculatePercentage(
                    parseFloat(loggedData.total_fat_logged || '0'), 
                    nutritionData.fat
                  )}%` 
                }} 
              />
            </div>
          </div>

          {/* GL */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-[#1F1F1F] text-[12px] 2xl:[14px] font-medium font-inter">
                GL
              </span>
              <span className="text-[#1F1F1F] text-[12px] 2xl:[14px] font-medium font-inter">
                {parseFloat(loggedData.total_GL_logged || '0').toFixed(0)}/{nutritionData.GL.toFixed(0)}
              </span>
            </div>
            <div className="w-full h-[3px] bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#A0CA07]" 
                style={{ 
                  width: `${calculatePercentage(
                    parseFloat(loggedData.total_GL_logged || '0'), 
                    nutritionData.GL
                  )}%` 
                }} 
              />
            </div>
          </div>

          {/* C:P:F */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-[#1F1F1F] text-[12px] 2xl:[14px] font-medium font-inter">
                C:P:F
              </span>
              <span className="text-[#1F1F1F] text-[12px] 2xl:[14px] font-medium font-inter">
                {loggedData.total_CPF || '0:0:0'} / {nutritionData.CPF}
              </span>
            </div>
            {/* <div className="w-full h-[3px] bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#007AFF]" 
                style={{ width: `${Math.min(cpfPercentage, 100)}%` }} 
              />
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionStats;